import { useEffect, useMemo, useRef } from 'react';
import { Animated, Image, StyleSheet, Text, View } from 'react-native';
import Svg, { Line, Path } from 'react-native-svg';
import { fontFamilies } from '../../utils/fontLoader';
import { responsiveFontSize } from '../../utils/responsiveHelpers';
import CountUp from '../common/CountUp';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Stroke-based glyphs from the V5 design (variants-1-3.jsx). They share a
// 24×24 viewBox, currentColor stroke, 1.7px stroke width, round caps/joins.
const ICON_STROKE = '#7AB7FF';
const ICON_BOX = 18;
const iconProps = {
  width: ICON_BOX,
  height: ICON_BOX,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: ICON_STROKE,
  strokeWidth: 1.7,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const ScanIcon = () => (
  <Svg {...iconProps}>
    <Path d="M3 7V5a2 2 0 0 1 2-2h2" />
    <Path d="M17 3h2a2 2 0 0 1 2 2v2" />
    <Path d="M21 17v2a2 2 0 0 1-2 2h-2" />
    <Path d="M7 21H5a2 2 0 0 1-2-2v-2" />
    <Line x1="7" y1="12" x2="17" y2="12" />
  </Svg>
);

const TrendIcon = () => (
  <Svg {...iconProps}>
    <Path d="M3 17l6-6 4 4 8-8" />
    <Path d="M14 7h7v7" />
  </Svg>
);

const BellIcon = () => (
  <Svg {...iconProps}>
    <Path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <Path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </Svg>
);

const ShieldIcon = () => (
  <Svg {...iconProps}>
    <Path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <Path d="M9 12l2 2 4-4" />
  </Svg>
);

/**
 * Shared visual atoms for the V5 signup gate and email-signup screens.
 *
 * The "encouragement" sidebar (TickerRow + DailyStack + AlertArtefact +
 * Testimonial) and trust strip live here so both screens render the same
 * value-density block on the right column. The host screen owns the layout
 * and the headline/form on the left.
 */

export const PulseDot = ({ color = '#5DD16B' }) => {
  const opacity = useRef(new Animated.Value(0.6)).current;
  useEffect(() => {
    if (process.env.NODE_ENV === 'test') return undefined;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, { toValue: 1, duration: 1200, useNativeDriver: false }),
        Animated.timing(opacity, { toValue: 0.55, duration: 1200, useNativeDriver: false }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [opacity]);
  return <Animated.View style={[atomStyles.pulseDot, { backgroundColor: color, opacity }]} />;
};

export const TrustStrip = ({ dense }) => (
  <View style={[atomStyles.trustStrip, dense && atomStyles.trustStripDense]}>
    <PulseDot />
    <Text style={[atomStyles.trustText, dense && atomStyles.trustTextDense]}>
      {dense ? 'No dealer money' : 'No dealer money. Ever.'}
    </Text>
  </View>
);

export const TickerRow = ({ stats, compact }) => {
  const cells = useMemo(() => {
    const out = [];
    // Order: average saving first, listings scanned middle, price drops last.
    // Each cell carries the raw `count` plus a `format` so the same CountUp
    // ticker used on /home can roll the number while preserving the £/k/+ unit.
    if (stats?.median_below_market_gbp) {
      out.push({
        count: stats.median_below_market_gbp,
        format: (n) => `£${n.toLocaleString('en-GB')}`,
        label: compact ? 'med. below market' : 'median below market',
      });
    }
    if (stats?.listings_scored_today) {
      out.push({
        count: stats.listings_scored_today,
        format: (n) => `${n.toLocaleString('en-GB')}+`,
        label: compact ? 'scanned daily' : 'UK listings scanned daily',
      });
    }
    if (stats?.price_drops_7d) {
      out.push({
        count: stats.price_drops_7d,
        format: (n) => n.toLocaleString('en-GB'),
        label: compact ? 'drops 7d' : 'price drops, last 7d',
      });
    }
    return out;
  }, [stats, compact]);

  if (cells.length === 0) return null;
  // Honour prefers-reduced-motion: render the final value with no roll.
  const animate = !prefersReducedMotion();
  return (
    <View style={atomStyles.tickerRow}>
      {cells.map((c, i) => (
        <View key={i} style={atomStyles.tickerCell}>
          <CountUp
            value={c.count}
            format={c.format}
            animate={animate}
            duration={1200}
            style={atomStyles.tickerNum}
          />
          <Text style={atomStyles.tickerLabel}>{c.label}</Text>
        </View>
      ))}
    </View>
  );
};

const STACK_ROWS = [
  { Icon: ScanIcon, text: 'Scan all UK listings every morning.' },
  { Icon: TrendIcon, text: 'Score each one against your brief.' },
  { Icon: BellIcon, text: 'Alert you on price drops or new matches.' },
  { Icon: ShieldIcon, text: 'Never take dealer money. Ever.' },
];

export const DailyStack = () => (
  <View style={atomStyles.stack}>
    {STACK_ROWS.map((r, i) => {
      const { Icon } = r;
      return (
        <View key={i} style={[atomStyles.stackRow, i === STACK_ROWS.length - 1 && atomStyles.stackRowLast]}>
          <View style={atomStyles.stackIcon}>
            <Icon />
          </View>
          <Text style={atomStyles.stackText}>{r.text}</Text>
        </View>
      );
    })}
  </View>
);

export const AlertArtefact = ({ budgetUpper, model }) => {
  const baseB = budgetUpper ?? 15000;
  const pre = (baseB - 200).toLocaleString('en-GB');
  const post = (baseB - 800).toLocaleString('en-GB');
  const budgetLabel = baseB >= 1000 ? `£${Math.round(baseB / 1000)}k` : `£${baseB}`;
  // Onboarding stores models as { make, model } — pull the first one. We
  // know what the user is shopping for in this flow, so the artefact names
  // it explicitly (no year — year is the only field we don't have).
  const carLabel = model?.make && model?.model
    ? `${model.make} ${model.model}`
    : 'Toyota RAV4';
  return (
    <View style={atomStyles.alertCard}>
      <Image
        source={require('../../../assets/icons/favicon.png')}
        style={atomStyles.alertApp}
        resizeMode="cover"
      />
      <View style={atomStyles.alertMeta}>
        <View style={atomStyles.alertRow1}>
          <Text style={atomStyles.alertAppName}>DOOCARS</Text>
          <Text style={atomStyles.alertNow}>now</Text>
        </View>
        <Text style={atomStyles.alertTitle}>Price drop on your #1 match</Text>
        <Text style={atomStyles.alertBody}>
          <Text style={atomStyles.alertBold}>{carLabel}</Text>
          {' · '}
          <Text style={atomStyles.alertBold}>£{pre} → £{post}</Text>
          {' · within your '}{budgetLabel}{' budget.'}
        </Text>
      </View>
    </View>
  );
};

export const Testimonial = ({ testimonial }) => {
  const initial = (testimonial.name || '?').charAt(0).toUpperCase();
  const hue = testimonial.avatarHue ?? 220;
  return (
    <View style={atomStyles.tmonial}>
      <Text style={atomStyles.tmonialQ}>"{testimonial.quote}"</Text>
      <View style={atomStyles.tmonialByline}>
        {testimonial.avatarSrc ? (
          <Image source={testimonial.avatarSrc} style={atomStyles.tmonialAvatarImg} />
        ) : (
          <View
            style={[
              atomStyles.tmonialAvatar,
              { backgroundColor: `hsl(${hue}, 55%, 35%)` },
            ]}
          >
            <Text style={atomStyles.tmonialAvatarText}>{initial}</Text>
          </View>
        )}
        <Text style={atomStyles.tmonialBylineText}>
          {testimonial.name}, {testimonial.age} · {testimonial.purchase}
        </Text>
      </View>
    </View>
  );
};

export const SectionLabel = ({ children, style }) => (
  <Text style={[atomStyles.sectionLabel, style]}>{children}</Text>
);

/**
 * V5Sidebar — composed right-column block. Used on the gate and email-signup
 * to keep the encouragement consistent across both screens.
 */
export const V5Sidebar = ({ budgetUpper, model, testimonial }) => (
  <>
    <View style={{ gap: 12 }}>
      <SectionLabel>Once you're in</SectionLabel>
      <DailyStack />
    </View>
    <View style={{ gap: 12, paddingTop: 10 }}>
      <SectionLabel>What tomorrow looks like</SectionLabel>
      <AlertArtefact budgetUpper={budgetUpper} model={model} />
    </View>
    <Testimonial testimonial={testimonial} />
  </>
);

export const FinePrint = ({ onTerms, onPrivacy }) => (
  <Text style={atomStyles.finePrint}>
    By signing up, you agree to our{' '}
    <Text onPress={onTerms} style={atomStyles.finePrintLink}>Terms of Service</Text>{' '}
    and{' '}
    <Text onPress={onPrivacy} style={atomStyles.finePrintLink}>Privacy Policy</Text>.
  </Text>
);

export const Headline = ({ title, sub, mobile }) => (
  <View style={{ gap: 4, alignItems: 'center' }}>
    {title ? (
      <Text style={[atomStyles.h1, mobile && atomStyles.h1Mobile]}>{title}</Text>
    ) : null}
    {sub ? (
      <Text style={[atomStyles.h1Light, mobile && atomStyles.h1LightMobile]}>{sub}</Text>
    ) : null}
  </View>
);

const atomStyles = StyleSheet.create({
  pulseDot: { width: 7, height: 7, borderRadius: 4 },
  trustStrip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 7,
    paddingHorizontal: 14,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  trustStripDense: { paddingVertical: 5, paddingHorizontal: 10 },
  trustText: {
    fontFamily: fontFamilies.satoshi.medium,
    fontSize: 12,
    color: 'rgba(255,255,255,0.85)',
  },
  trustTextDense: { fontSize: 10.5 },

  tickerRow: {
    flexDirection: 'row',
    gap: 24,
    paddingVertical: 18,
    width: 600,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderRightWidth: 0,
    borderTopColor: 'rgba(255,255,255,0.3)',
    borderBottomColor: 'rgba(255,255,255,0.3)',
  },
  tickerCell: { flex: 1, alignItems: 'center' },
  tickerNum: {
    fontFamily: fontFamilies.satoshi.regular,
    fontSize: 26,
    color: '#FFFFFF',
    letterSpacing: -0.4,
    textAlign: 'center',
  },
  tickerLabel: {
    fontFamily: fontFamilies.satoshi.bold,
    fontSize: 9,
    color: 'rgba(255,255,255,0.5)',
    letterSpacing: 0,
    textTransform: 'uppercase',
    marginTop: 2,
    textAlign: 'center',
  },

  sectionLabel: {
    fontFamily: fontFamilies.satoshi.bold,
    fontSize: 10.5,
    letterSpacing: 0.84,
    color: 'rgba(255,255,255,0.4)',
    textTransform: 'uppercase',
  },

  stack: {
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    paddingVertical: 5,
  },
  stackRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 13,
    borderBottomWidth: 1,
    borderColor: 'rgba(255,255,255,0.06)',
  },
  stackRowLast: { borderBottomWidth: 0 },
  stackIcon: {
    width: 32, height: 32, borderRadius: 8,
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)',
    alignItems: 'center', justifyContent: 'center',
  },
  stackText: {
    flex: 1,
    fontFamily: fontFamilies.satoshi.regular,
    fontSize: 14,
    color: 'rgba(255,255,255,0.85)',
    lineHeight: 20,
  },

  alertCard: {
    flexDirection: 'row',
    gap: 10,
    padding: 17,
    backgroundColor: 'rgba(20,20,22,0.85)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.10)',
    borderRadius: 16,
  },
  alertApp: { width: 36, height: 36, borderRadius: 8 },
  alertMeta: { flex: 1 },
  alertRow1: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 2,
  },
  alertAppName: {
    fontFamily: fontFamilies.satoshi.bold,
    fontSize: 10.5,
    color: '#FFFFFF',
    letterSpacing: 0.4,
  },
  alertNow: {
    fontFamily: fontFamilies.satoshi.regular,
    fontSize: 11,
    color: 'rgba(255,255,255,0.5)',
  },
  alertTitle: {
    fontFamily: fontFamilies.satoshi.bold,
    fontSize: 13,
    color: '#FFFFFF',
    marginBottom: 2,
  },
  alertBody: {
    fontFamily: fontFamilies.satoshi.regular,
    fontSize: 12.5,
    color: 'rgba(255,255,255,0.85)',
    lineHeight: 17,
  },
  alertBold: {
    fontFamily: fontFamilies.satoshi.bold,
    color: '#FFFFFF',
  },

  tmonial: {
    padding: 17,
    borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.025)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    marginTop: 8,
  },
  tmonialQ: {
    fontFamily: fontFamilies.satoshi.regular,
    fontSize: 13.5,
    color: 'rgba(255,255,255,0.85)',
    lineHeight: 20,
  },
  tmonialByline: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 10,
  },
  tmonialAvatar: {
    width: 26,
    height: 26,
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tmonialAvatarImg: {
    width: 26,
    height: 26,
    borderRadius: 13,
  },
  tmonialAvatarText: {
    fontFamily: fontFamilies.satoshi.bold,
    fontSize: 12,
    color: '#FFFFFF',
    letterSpacing: 0.2,
  },
  tmonialBylineText: {
    fontFamily: fontFamilies.satoshi.regular,
    fontSize: 12,
    color: 'rgba(255,255,255,0.5)',
  },

  finePrint: {
    fontFamily: fontFamilies.satoshi.regular,
    fontSize: 12,
    color: 'rgba(255,255,255,0.5)',
    lineHeight: 18,
    textAlign: 'center',
  },
  finePrintLink: {
    color: 'rgba(255,255,255,0.65)',
    textDecorationLine: 'underline',
  },

  h1: {
    fontFamily: fontFamilies.satoshi.medium,
    fontSize: responsiveFontSize(44),
    lineHeight: responsiveFontSize(44) * 1.1,
    letterSpacing: -0.4,
    color: '#FFFFFF',
    textAlign: 'center',
  },
  // Mobile headline: 32px with roomy line spacing so wrapped lines breathe.
  h1Mobile: { fontSize: 32, lineHeight: 42 },
  h1Light: {
    fontFamily: fontFamilies.satoshi.regular,
    fontSize: responsiveFontSize(44),
    lineHeight: responsiveFontSize(44) * 1.1,
    letterSpacing: -0.4,
    color: '#FFFFFF',
    textAlign: 'center',
  },
  // Mobile subheadline: small + grey so the benefits read as support copy, not a second headline.
  h1LightMobile: {
    fontSize: 14,
    lineHeight: 20,
    letterSpacing: 0,
    color: 'rgba(255,255,255,0.6)',
    paddingTop: 16,
  },
});
